<template>
    <div id="iframe-wrapper" class="h-full relative">
        <img
            class="loading absolute"
            src="https://abroad-sin-static.shengqugames-corp.com/wpay/icon_loading.png"
            alt="loading"
        />
    </div>
</template>

<script>
// OAuth 授权返回的 redirect_uri 界面
import Vue from 'vue';
import { Dialog } from 'vant';
import Cookies from 'js-cookie';
import Logs from '@/lib/logs';
import useOAuth from '@/mixins/useOAuth';
import useTransfer from '@/mixins/useTransfer';
import { Platform, OAuthKey, OAuthError } from '@/enums';
import { jsonParser, queryConcat /* , searchParams */ } from '@/lib/utils';
import get from 'lodash/get';

Vue.use(Dialog);

export default {
    mixins: [useOAuth, useTransfer],
    data() {
        return {
            key: null,
            appId: null,
            returnUrl: null,
            action: null,
            // 埋点日志:
            logs: { web_pageid: 'ghome-web-oauth-login' },
        };
    },
    created() {
        this.setData();
    },
    mounted() {
        // fix: 避免在客户端中因资源未完全加载就调用回调导致的 AuthCallback 丢失问题
        window.addEventListener('load', this.handleOAuth);
    },
    beforeDestroy() {
        localStorage.clear();
        window.removeEventListener('load', this.handleOAuth);
    },
    methods: {
        setData() {
            this.key = this.$route.params.key;
            this.appId = jsonParser(
                sessionStorage.getItem('appId') || localStorage.getItem('appId')
            );
            this.returnUrl = jsonParser(
                sessionStorage.getItem('returnUrl') || localStorage.getItem('returnUrl')
            );
            this.action = jsonParser(
                sessionStorage.getItem('emailType') || localStorage.getItem('emailType')
            );
        },

        // 处理 OAuth
        handleOAuth() {
            if (/code|token/.test(this.$route.fullPath)) {
                this.handleSuccess();
            } else {
                this.handleFailure();
            }
        },

        // OAuth 授权成功
        async handleSuccess() {
            try {
                // step 1: 从 redirect_uri 中获取授权参数
                const tokenInfo = await this.getAccessToken(this.key);

                // step 2.1: WebView 或 Android (apple 登录) 直接返回 OAuth 给的参数，无需去后端登录
                if (
                    this.platform === Platform.WebView ||
                    (this.platform === Platform.Android && this.key === OAuthKey.Apple)
                ) {
                    console.log('client');
                    this.webviewCallback(this.platform, {
                        code: 0,
                        data: tokenInfo,
                        msg: 'SUCCESS',
                    });
                } else if (
                    [Platform.H5Transfer, Platform.WebViewTransfer].includes(this.platform)
                ) {
                    // step 2.2: Tencent LI 账号迁移，登录成功，跳转到结果页
                    const accountInfo = jsonParser(sessionStorage.getItem('accountInfo')) || {};
                    const query = {
                        appId: this.appId,
                        platform: this.platform,
                        ...accountInfo,
                    };
                    this.loginByOAuth(this.key, tokenInfo)
                        .then(() => {
                            Logs.imgLog_Button({
                                ...this.logs,
                                web_buttonid: 'oauth-login',
                                web_userid: `cookie=${Cookies.get('WEB_TOKEN')}`,
                            });

                            this.transferAccount(query);
                        })
                        .catch(() => {
                            this.$router.replace({
                                name: 'transferLogin',
                                query,
                            });
                        });
                } else if (this.platform === Platform.Iframe) {
                    // step 2.3: iframe 登录后跳转回原页面
                    this.loginByOAuth(this.key, tokenInfo, { disabledToast: true })
                        .then(userInfo => {
                            Logs.imgLog_Button({
                                ...this.logs,
                                web_buttonid: 'oauth-login',
                                web_userid: `cookie=${Cookies.get('WEB_TOKEN')}`,
                            });

                            const { token, ticket, userId, loginType } = userInfo;
                            const parameters = queryConcat(
                                { token, ticket, userId, loginType },
                                /\?/.test(this.returnUrl) ? '&' : '?',
                                false
                            );
                            window.location.replace(this.returnUrl + parameters);
                        })
                        .catch(err => {
                            const error = get(err, 'data.msg', 'login failed');
                            const errorCode = get(err, 'data.code', 500);
                            Dialog.alert({
                                message: `${error} \n${errorCode}`,
                                theme: 'round-button',
                                confirmButtonText: this.$t('common.back'),
                                confirmButtonColor: '#e4004f',
                                getContainer: '#iframe-wrapper',
                            }).then(() => {
                                const backUrl = jsonParser.parse(sessionStorage.getItem('backUrl'));
                                if (backUrl) window.location.replace(backUrl);
                            });
                        });
                } else {
                    // step 2.4: h5 登录后跳转
                    this.loginByOAuth(this.key, tokenInfo)
                        .then(userInfo => {
                            Logs.imgLog_Button({
                                ...this.logs,
                                web_buttonid: 'oauth-login',
                                web_userid: `cookie=${Cookies.get('WEB_TOKEN')}`,
                            });

                            const { token, ticket, userId, loginType } = userInfo;
                            const parameters = queryConcat(
                                { token, ticket, userId, loginType },
                                /\?/.test(this.returnUrl) ? '&' : '?',
                                false
                            );
                            window.location.replace(this.returnUrl + parameters);
                        })
                        .catch(() => {
                            this.$router.replace({
                                name: 'Login',
                                query: {
                                    appId: this.appId,
                                    platform: this.platform,
                                    action: this.action,
                                    return_url: encodeURIComponent(this.returnUrl),
                                },
                            });
                        });
                }
            } catch (error) {
                console.log('handleSuccess catch: ', error);
                // 针对 Line 和 Twitter 获取 token 的请求错误
                // Error 1：Twitter 重复登录错误，原因：浏览器后退操作，方案：返回首页
                if (error.msg === OAuthError.TwitterRepeatLogin.msg) {
                    const backUrl = jsonParser(localStorage.getItem('backUrl'));
                    if (backUrl) window.location.replace(backUrl);
                }
            }
        },

        // OAuth 授权失败
        handleFailure() {
            const { error = '' } = this.errorHandler(this.key);
            if (
                this.platform === Platform.WebView ||
                (this.platform === Platform.Android && this.key === OAuthKey.Apple)
            ) {
                this.webviewCallback(this.platform, {
                    code: -1,
                    data: {},
                    msg: error,
                });
            } else if ([Platform.H5Transfer, Platform.WebViewTransfer].includes(this.platform)) {
                const accountInfo = jsonParser(sessionStorage.getItem('accountInfo')) || {};
                this.$toast(error);
                this.$router.replace({
                    name: 'transferLogin',
                    query: {
                        appId: this.appId,
                        platform: this.platform,
                        ...accountInfo,
                    },
                });
            } else if (this.platform === Platform.Iframe) {
                Dialog.alert({
                    message: `${error}`,
                    theme: 'round-button',
                    confirmButtonText: this.$t('common.back'),
                    confirmButtonColor: '#e4004f',
                    getContainer: '#iframe-wrapper',
                }).then(() => {
                    const backUrl = jsonParser.parse(sessionStorage.getItem('backUrl'));
                    if (backUrl) window.location.replace(backUrl);
                });
            } else {
                this.$toast(error);
                this.$router.replace({
                    name: 'Login',
                    query: {
                        appId: this.appId,
                        platform: this.platform,
                        action: this.action,
                        return_url: encodeURIComponent(this.returnUrl),
                    },
                });
            }
        },
        webviewCallback(platform, payload = {}) {
            // AuthCallback 不能及时拿到，暂时先加个延时
            switch (platform) {
                case Platform.WebView:
                    setTimeout(() => {
                        try {
                            window.AuthCallback(payload);
                        } catch (e) {
                            console.error(e);
                        }
                    }, 800);
                    break;
                case Platform.Android:
                    setTimeout(() => {
                        try {
                            window.appleLoginJsBridge.AuthCallback(JSON.stringify(payload));
                        } catch (e) {
                            console.error(e);
                        }
                    }, 800);
                    break;
                default:
                    break;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.loading {
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
}
</style>
